import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../utils/AuthContext';
import { isHtmlEmpty } from '../utils/richComment';
import {
  COMMENT_TYPING_HEARTBEAT_MS,
  clearCommentTyping,
  firstNameFromDisplayName,
  formatCommentTypingLabel,
  isFreshCommentTypist,
  publishCommentTyping,
  subscribeCommentTyping,
  type CommentTypist,
} from '../utils/commentTypingService';

export function useCommentTyping(threadKey: string, composerHtml: string, enabled = true): string {
  const { user } = useAuth();
  const uid = user?.id || '';
  const firstName = firstNameFromDisplayName(user?.name);
  const [peers, setPeers] = useState<CommentTypist[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const lastPublishRef = useRef(0);
  const typingRef = useRef(false);

  const isTyping = enabled && !!threadKey && !!uid && !isHtmlEmpty(composerHtml);

  useEffect(() => {
    if (!enabled || !threadKey || !uid) {
      setPeers([]);
      return;
    }
    return subscribeCommentTyping(threadKey, uid, setPeers);
  }, [enabled, threadKey, uid]);

  useEffect(() => {
    if (peers.length === 0) return;
    const id = window.setInterval(() => setNow(Date.now()), 2000);
    return () => window.clearInterval(id);
  }, [peers.length]);

  useEffect(() => {
    if (!threadKey || !uid) return;

    if (!enabled || !isTyping) {
      if (typingRef.current) {
        typingRef.current = false;
        void clearCommentTyping(threadKey, uid);
      }
      return;
    }

    const publish = () => {
      lastPublishRef.current = Date.now();
      typingRef.current = true;
      void publishCommentTyping(threadKey, uid, firstName);
    };

    const elapsed = Date.now() - lastPublishRef.current;
    if (!typingRef.current || elapsed >= COMMENT_TYPING_HEARTBEAT_MS) {
      publish();
    }

    const id = window.setInterval(publish, COMMENT_TYPING_HEARTBEAT_MS);
    return () => window.clearInterval(id);
  }, [enabled, firstName, isTyping, threadKey, uid]);

  useEffect(() => {
    return () => {
      if (threadKey && uid && typingRef.current) {
        void clearCommentTyping(threadKey, uid);
      }
    };
  }, [threadKey, uid]);

  return useMemo(() => {
    const fresh = peers.filter(t => isFreshCommentTypist(t, now));
    return formatCommentTypingLabel(fresh.map(t => t.name));
  }, [now, peers]);
}
